// src/components/Inicio/Inicial.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Bienvenida from "./Bienvenida";
import Resumen from "./Resumen";
import MisSensores from "./MisSensores";
import MisEstadisticas from "./MisEstadisticas";
import usuarioService from "../../services/usuarioService";
import { usePermisos } from "../../context/PermisosContext";

const Inicial = () => {
  const { can, loading: loadingPermisos } = usePermisos();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [perfil, setPerfil] = useState({
    id: null,
    nombre: "",
    email: "",
    rol: "",
    esTemporal: false,
  });

  // ------ Permisos de pestañas ------
  const puedeVerInicio = useMemo(() => can("INICIO_VIEW"), [can]);
  const puedeVerMisSensores = useMemo(
    () => can("INICIO_TAB_MIS_SENSORES_VIEW"),
    [can]
  );
  const puedeVerMisEstadisticas = useMemo(
    () => can("INICIO_TAB_MIS_ESTADISTICAS_VIEW"),
    [can]
  );

  const puedeVerResumen = can("INICIO_TAB_RESUMEN_VIEW");
  const puedeVerBienvenida = can("INICIO_TAB_BIENVENIDA_VIEW");

  // Orden/whitelist de subpestañas válidas según permisos actuales
  const allowedSubs = useMemo(() => {
    const list = [];
    if (puedeVerMisEstadisticas) list.push("mis-estadisticas");
    if (puedeVerMisSensores) list.push("mis-sensores");
    if (puedeVerResumen) list.push("resumen");
    if (puedeVerBienvenida) list.push("bienvenida");
    return list;
  }, [puedeVerMisEstadisticas, puedeVerMisSensores, puedeVerResumen, puedeVerBienvenida]);

  // Sub actual desde URL (sin estado local innecesario)
  const sub = searchParams.get("sub") || "";

  // Normaliza sub al primero permitido cuando:
  // - no hay `sub` en URL
  // - o `sub` no pertenece a allowedSubs
  useEffect(() => {
    if (loadingPermisos || !allowedSubs.length) return;

    // Al cambiar de menú, este componente puede permanecer montado hasta el
    // siguiente render. Nunca debe escribir su `sub` sobre la URL de Sensores
    // (u otro módulo) durante esa transición.
    const menuEnUrl = searchParams.get("tab") || "Inicio";
    if (menuEnUrl !== "Inicio") return;

    const isValid = sub && allowedSubs.includes(sub);
    if (!isValid) {
      const nextSub = allowedSubs[0] || "bienvenida";
      const next = new URLSearchParams(searchParams);
      next.set("sub", nextSub);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPermisos, sub, allowedSubs]);

  // Helper para cambiar `sub` sin mutar `searchParams` original
  const setSub = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("sub", value);
    setSearchParams(next, { replace: true });
  };

  // ------ Perfil ------
  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        setCargando(true);
        setError("");
        const { data } = await usuarioService.obtenerPerfil();

        const rolLS =
          window.localStorage.getItem("rol") ||
          window.localStorage.getItem("Rol") ||
          "";

        setPerfil({
          id: data?.id ?? null,
          nombre: data?.nombre ?? "",
          email: data?.email ?? "",
          rol: data?.rol || rolLS || (data?.esTemporal ? "Usuario Temporal" : "Usuario"),
          esTemporal: !!data?.esTemporal,
        });
      } catch (e) {
        console.error(e);
        setError("No se pudo cargar el perfil.");
      } finally {
        setCargando(false);
      }
    };
    fetchPerfil();
  }, []);

  if (cargando || loadingPermisos) {
    return (
      <div className="min-h-[50dvh] md:min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="animate-pulse text-neutral-300">Cargando Inicio…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50dvh] md:min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="bg-red-900/30 border border-red-600/60 text-red-100 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!puedeVerInicio) {
    return (
      <div className="min-h-[50dvh] md:min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="bg-neutral-900/70 border border-neutral-700 text-neutral-200 px-4 py-4 rounded-lg max-w-lg text-center">
          <h4 className="text-lg font-semibold text-red-300 mb-1">Acceso restringido</h4>
          <p className="text-sm text-neutral-300">No tienes permisos para ver esta sección de Inicio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[50dvh] md:min-h-screen bg-neutral-950 text-white py-6 sm:py-10 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="mb-4 flex items-center gap-2 overflow-x-auto">
          {/* 1) Mis Estadísticas */}
          {puedeVerMisEstadisticas && (
            <button
              type="button"
              onClick={() => setSub("mis-estadisticas")}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                sub === "mis-estadisticas"
                  ? "bg-emerald-600/90 hover:bg-emerald-600 border-emerald-500/60"
                  : "bg-neutral-900 border-neutral-700 hover:bg-neutral-800"
              }`}
            >
              Mis Estadísticas
            </button>
          )}

          {/* 2) Mis Sensores */}
          {puedeVerMisSensores && (
            <button
              type="button"
              onClick={() => setSub("mis-sensores")}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                sub === "mis-sensores"
                  ? "bg-emerald-600/90 hover:bg-emerald-600 border-emerald-500/60"
                  : "bg-neutral-900 border-neutral-700 hover:bg-neutral-800"
              }`}
            >
              Mis Sensores
            </button>
          )}

          {/* 3) Resumen */}
          {puedeVerResumen && <button
            type="button"
            onClick={() => setSub("resumen")}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              sub === "resumen"
                ? "bg-emerald-600/90 hover:bg-emerald-600 border-emerald-500/60"
                : "bg-neutral-900 border-neutral-700 hover:bg-neutral-800"
            }`}
          >
            Resumen
          </button>}

          {/* 4) ¿Quiénes Somos? */}
          {puedeVerBienvenida && <button
            type="button"
            onClick={() => setSub("bienvenida")}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              sub === "bienvenida"
                ? "bg-emerald-600/90 hover:bg-emerald-600 border-emerald-500/60"
                : "bg-neutral-900 border-neutral-700 hover:bg-neutral-800"
            }`}
          >
            ¿Quiénes Somos?
          </button>}
        </div>

        <div className="bg-neutral-900/70 border border-emerald-700/30 rounded-xl p-4 sm:p-6">
          {sub === "mis-estadisticas" && puedeVerMisEstadisticas ? (
            <MisEstadisticas />
          ) : sub === "mis-sensores" && puedeVerMisSensores ? (
            <MisSensores />
          ) : sub === "resumen" && puedeVerResumen ? (
            <Resumen perfil={perfil} />
          ) : puedeVerBienvenida ? (
            <Bienvenida
              nombre={perfil.nombre}
              rol={perfil.rol}
              esTemporal={perfil.esTemporal}
              email={perfil.email}
            />
          ) : <p>No tienes secciones habilitadas en Inicio.</p>}
        </div>
      </div>
    </div>
  );
};

export default Inicial;
